import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api';
import { useLanguage } from '../../i18n/LanguageContext';
import './SystemLogs.css';

const LEVELS = ['CRITICAL', 'ERROR', 'WARNING', 'NOTICE', 'INFO', 'DEBUG'];

const formatSize = (bytes = 0) => {
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
};

const SystemLogs = () => {
    const { t } = useLanguage();
    const [files, setFiles] = useState([]);
    const [totalSize, setTotalSize] = useState(0);
    const [selected, setSelected] = useState(null);
    const [entries, setEntries] = useState([]);
    const [meta, setMeta] = useState({ counts: {}, truncated: false });
    const [level, setLevel] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notice, setNotice] = useState({ type: '', text: '' });

    const loadFiles = useCallback(async () => {
        try {
            const res = await api.getSystemLogFiles();
            const list = res.data || [];
            setFiles(list);
            setTotalSize(res.total_size || 0);
            setSelected((current) => (current && list.some((f) => f.name === current) ? current : list[0]?.name || null));
        } catch (err) {
            setNotice({ type: 'error', text: err.message });
        } finally {
            setLoading(false);
        }
    }, []);

    const loadEntries = useCallback(async () => {
        if (!selected) { setEntries([]); return; }
        try {
            const params = {};
            if (level) params.level = level;
            if (search.trim()) params.search = search.trim();
            const res = await api.getSystemLog(selected, params);
            setEntries(res.data || []);
            setMeta({ counts: res.counts || {}, truncated: !!res.truncated });
            setExpanded(null);
        } catch (err) {
            setNotice({ type: 'error', text: err.message });
        }
    }, [selected, level, search]);

    useEffect(() => { loadFiles(); }, [loadFiles]);
    useEffect(() => { loadEntries(); }, [loadEntries]);

    const deleteFile = async () => {
        if (!selected || !window.confirm(t('Delete this log file? This cannot be undone.'))) return;
        try {
            const res = await api.deleteSystemLog(selected);
            setNotice({ type: 'success', text: t(res.message) });
            setSelected(null);
            loadFiles();
        } catch (err) {
            setNotice({ type: 'error', text: err.message });
        }
    };

    const clearAll = async () => {
        if (!window.confirm(t('Delete ALL system log files? This cannot be undone.'))) return;
        try {
            const res = await api.clearSystemLogs();
            setNotice({ type: 'success', text: `${res.deleted ?? 0} ${t('log files deleted.')}` });
            setSelected(null);
            loadFiles();
        } catch (err) {
            setNotice({ type: 'error', text: err.message });
        }
    };

    const onSearch = (e) => {
        e.preventDefault();
        setSearch(e.target.elements.search.value);
    };

    return (
        <div className="system-logs-page">
            <div className="system-logs-header">
                <div>
                    <h1>{t('System Logs')}</h1>
                    <p>{t('Errors and events recorded by the server')} · {files.length} {t('files')} · {formatSize(totalSize)}</p>
                </div>
                <div className="system-logs-actions">
                    <button className="btn btn-secondary" onClick={() => { loadFiles(); loadEntries(); }}>{t('Refresh')}</button>
                    <button className="btn btn-danger" disabled={!files.length} onClick={clearAll}>{t('Clear all logs')}</button>
                </div>
            </div>

            {notice.text && <div className={`system-logs-notice ${notice.type}`} onClick={() => setNotice({ type: '', text: '' })}>{notice.text}</div>}

            {loading ? (
                <p className="system-logs-empty">{t('Loading...')}</p>
            ) : !files.length ? (
                <p className="system-logs-empty">{t('No log files.')}</p>
            ) : (
                <div className="system-logs-layout">
                    <aside className="system-logs-files">
                        {files.map((file) => (
                            <button key={file.name} className={file.name === selected ? 'active' : ''} onClick={() => setSelected(file.name)}>
                                <b>{file.date}</b><span>{formatSize(file.size)}</span>
                            </button>
                        ))}
                    </aside>

                    <section className="system-logs-viewer">
                        <div className="system-logs-toolbar">
                            <form onSubmit={onSearch}>
                                <input name="search" type="search" defaultValue={search} placeholder={t('Search messages')} />
                                <button type="submit" className="btn btn-primary">{t('Search')}</button>
                            </form>
                            <select value={level} onChange={(e) => setLevel(e.target.value)}>
                                <option value="">{t('All levels')}</option>
                                {LEVELS.map((l) => <option key={l} value={l}>{l} ({meta.counts[l] || 0})</option>)}
                            </select>
                            <button className="btn btn-danger" onClick={deleteFile}>{t('Delete this file')}</button>
                        </div>
                        {meta.truncated && <p className="system-logs-hint">{t('This file is large; only its most recent part is shown.')}</p>}
                        {entries.length === 0 ? (
                            <p className="system-logs-empty">{t('No entries match.')}</p>
                        ) : (
                            <ul className="system-logs-entries">
                                {entries.map((entry, index) => (
                                    <li key={index} onClick={() => entry.details && setExpanded(expanded === index ? null : index)} className={entry.details ? 'expandable' : ''}>
                                        <div className="system-logs-line">
                                            <span className={`system-logs-level ${entry.level.toLowerCase()}`}>{entry.level}</span>
                                            <time>{entry.time}</time>
                                            <span className="system-logs-message">{entry.message}</span>
                                        </div>
                                        {expanded === index && <pre>{entry.details}</pre>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
};

export default SystemLogs;
